import { useEffect, useState } from 'react';
import type { InstructorDashboard } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';

export function InstructorDashboardPage() {
  const [data, setData] = useState<InstructorDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .instructorDashboard()
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (loading) return <LoadingState message="Loading instructor dashboard…" />;
  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!data) return null;

  return (
    <div className="page">
      <PageHeader
        title="Instructor Dashboard"
        subtitle={`${data.programName} · ${data.organizationName}`}
      />

      <div className="stat-grid">
        <div className="card stat-card">
          <span className="stat-card__label">Total students</span>
          <span className="stat-card__value">{data.totalStudents}</span>
        </div>
        <div className="card stat-card">
          <span className="stat-card__label">Active cohorts</span>
          <span className="stat-card__value">
            {data.cohorts.filter((c) => c.status === 'active').length}
          </span>
        </div>
      </div>

      <section className="card">
        <h2 className="card__title">Cohorts</h2>
        {data.cohorts.length === 0 ? (
          <p className="text-muted">No cohorts in your program yet.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Students</th>
                </tr>
              </thead>
              <tbody>
                {data.cohorts.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>
                      <StatusBadge status={c.status} />
                    </td>
                    <td>{c.studentCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
